import * as React from "react";

import { AIChatContext } from "@/Chat/ChatSidebar/AIChatContext";
import { ContextType } from "@/Chat/ChatSidebar/contextType";

/**
 * Provides a minimal, read-only AIChatContext for rendering ChatByIdView
 * outside of the normal chat sidebar (e.g. in admin views).
 *
 * Most mutation handlers are no-ops and interactive features are disabled.
 * ``handleHistoryChatClicked`` is the one exception: ``ForkChatButton``
 * calls it on a successful fork to navigate the viewer to the new chat,
 * so the parent can pass ``onChatClicked`` to receive that signal.
 */
interface ReadOnlyAIChatProviderProps {
  children: React.ReactNode;
  /** Called when a child requests navigation to a different chat (today,
   *  only ``ForkChatButton`` after a successful fork). When omitted the
   *  request is dropped silently. */
  onChatClicked?: (chatId: string) => void;
}

export const ReadOnlyAIChatProvider: React.FC<ReadOnlyAIChatProviderProps> = ({
  children,
  onChatClicked,
}) => {
  const noop = React.useCallback(() => {}, []);

  const handleHistoryChatClicked = React.useCallback(
    (chatId: string) => {
      onChatClicked?.(chatId);
    },
    [onChatClicked],
  );

  const value = React.useMemo(
    () => ({
      isFullscreen: false,
      showHistory: false,
      autoEnabled: false,
      currentChatId: undefined,
      context: { dataset_ids: [], file_ids: [] },
      contextDisplayNames: { datasets: {}, files: {} },
      modelProfile: undefined,

      setIsFullscreen: noop as (value: boolean) => void,
      setShowHistory: noop as (value: boolean) => void,
      setAutoEnabled: noop as (value: boolean) => void,
      setCurrentChatId: noop as (chatId: string | undefined) => void,
      setModelProfile: noop as (profile: string) => void,

      handleContextDelete: noop as (
        contextType: ContextType,
        contextContent: string,
      ) => void,
      handleNewChat: noop,
      handleToggleFullscreen: noop,
      handleToggleHistory: noop,
      handleToggleAuto: noop,
      handleHistoryChatClicked,
      handleLinkClick: noop,
      handleResetUIState: noop,
    }),
    [noop, handleHistoryChatClicked],
  );

  return (
    <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>
  );
};
